import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { authClient } from '../utils/amplifyClient';
import { useNetworkState } from '@uidotdev/usehooks';
import * as Sentry from '@sentry/browser';
import {
    listDiscrepancyByEquipmentIdAndUpdatedAt as discrepanciesByEquipmentIdQuery,
    reportsByEquipmentId as reportsByEquipmentIdQuery,
    reportsByUnitId as reportsByUnitIdQuery,
} from '../graphql/queries';
import { useUserContext } from '../contexts/UserContext';
import { ITEM_STATUS_CODES, LOCAL_STORAGE_KEYS } from '../utils/constants';
import { TEIQspinner } from '../components/ui';
import { Inspect } from '../components/Inspect';
import { ModelSortDirection, Report, SingleUnit } from '../types/schema';
import { tDiscrepancyItem, tGroupedEquipment } from '../types';

import '../css/InspectionOptions.css';
import { getUrl } from 'aws-amplify/storage';
import { DailyServiceLog } from '../components/DailyServiceLog';
import { useEquipmentContext } from '../contexts/EquipmentContext';
import { useFormsContext } from '../contexts/FormsContext';
import { useOptionalNavigationGuard } from '../contexts/NavigationGuardContext';
import { useRegionAndLocationContext } from '../contexts/RegionAndLocationContext';
import { InspectionOptionsHub } from '../components/inspection-flow/InspectionOptionsHub';
import { InspectionFlowUnitPicker } from '../components/inspection-flow/InspectionFlowUnitPicker';
import { InspectionFlowPageShell } from '../components/inspection-flow/InspectionFlowPageShell';
import { countOpenIssues } from '../components/inspection-flow/inspectionFlowUtils';
import { buildInspectSearchParams } from '../components/inspection-flow/inspectionFlowUrlUtils';
import {
    buildUnitReportsPath,
    seedUnitReportFilters,
} from '../utils/unitReportsUtils';
import {
    fetchLatestInspectionReportForUnit,
} from '../utils/reportUtils';
import { buildReportPath } from '../utils/reportUrlUtils';
import { useCompanyUnitsOfMeasure } from '../hooks/useCompanyUnitsOfMeasure';
import { loadInspectionDraft } from '../utils/inspectionDraftStorage';
import '../utils/inspectionDraftStorage';

export const InspectionOptions = () => {
    const { id: equipmentId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const guard = useOptionalNavigationGuard();
    const network = useNetworkState();
    const { company, companyOwner } = useUserContext();
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [discrepancyStatus, setDiscrepancyStatus] = useState<tDiscrepancyItem[] | null>(null);
    const [equipmentData, setEquipmentData] = useState<tGroupedEquipment | null>(null);
    const [lastReportData, setLastReportData] = useState<Report | null>(null);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [selectedUnitId, setSelectedUnitId] = useState<{ label: string; value: string } | null>(null);
    const [selectedUnit, setSelectedUnit] = useState<SingleUnit | null>(null);
    const [imageUrls, setImageUrls] = useState<{ [key: string]: string }>({});
    const [lastInspectionReport, setLastInspectionReport] = useState<Awaited<
        ReturnType<typeof fetchLatestInspectionReportForUnit>
    > | null>(null);
    const [_setIsPrivateBrowserModalOpen] = useState(false);
    const [_isPrivateBrowser, _setIsPrivateBrowser] = useState(false);
    /** When true, skip re-applying unitId from URL/localStorage (user backed to unit picker). */
    const skipUnitRestoreRef = useRef(false);

    const { companyGroupedEquipmentData } = useEquipmentContext();
    const { companyLocations } = useRegionAndLocationContext();
    const { companyForms } = useFormsContext();

    const companyId = (company?.id as string) || companyOwner?.companyId;
    const { catalogOptions: umCatalog } = useCompanyUnitsOfMeasure(
        companyId ?? '',
        selectedUnit?.unitOfMeasure ? [String(selectedUnit.unitOfMeasure)] : [],
    );

    const actionQuery = searchParams.get('action');

    const syncInspectUrl = useCallback(
        (updates: { unitId?: string | null; action?: string | null }) => {
            const next = buildInspectSearchParams(searchParams, updates);
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const handleExitInspectionToHub = useCallback(() => {
        setSelectedOption(null);
        syncInspectUrl({ action: null });
    }, [syncInspectUrl]);

    const handleInspectPageBack = useCallback(() => {
        if (selectedOption) {
            const returnTo = searchParams.get('returnTo');
            if (
                returnTo === 'equipment' &&
                (actionQuery === 'inspect' || actionQuery === 'dailyService')
            ) {
                const unitPath = selectedUnitId?.value ? `/equipment/${selectedUnitId.value}` : null;
                if (unitPath) {
                    if (guard) {
                        guard.tryNavigate(unitPath);
                    } else {
                        navigate(unitPath);
                    }
                    return;
                }

                if (guard) {
                    guard.tryGoBack('/');
                } else if (window.history.length > 1) {
                    navigate(-1);
                } else {
                    navigate('/');
                }
                return;
            }

            handleExitInspectionToHub();
            return;
        }

        if (guard) {
            guard.tryNavigate('/');
        } else {
            navigate('/');
        }
    }, [actionQuery, guard, handleExitInspectionToHub, navigate, searchParams, selectedOption, selectedUnitId]);

    const clearSelectedUnit = useCallback(() => {
        skipUnitRestoreRef.current = true;
        syncInspectUrl({ unitId: null, action: null });
        localStorage.removeItem(LOCAL_STORAGE_KEYS.SELECTED_UNIT);
        setSelectedUnitId(null);
        setSelectedUnit(null);
        setLastReportData(null);
        setLastInspectionReport(null);
    }, [syncInspectUrl]);

    const hasMultipleUnits = (equipmentData?.units?.length ?? 0) > 1;
    const needsUnitSelection = hasMultipleUnits && !selectedUnitId;

    const handleInspectionFlowNavBack = useCallback(() => {
        if (selectedUnitId && hasMultipleUnits) {
            clearSelectedUnit();
            return;
        }

        if (guard) {
            guard.tryNavigate('/');
        } else {
            navigate('/');
        }
    }, [clearSelectedUnit, guard, hasMultipleUnits, navigate, selectedUnitId]);

    const inspectionFlowBackLabel = needsUnitSelection || !hasMultipleUnits ? 'Back to Home' : 'Back';
    const inspectionFormName =
        companyForms.find((form) => form.id === equipmentData?.formId)?.name || 'Inspection Form';
    const dailyServiceFormId = selectedUnit?.dailyServiceLogFormId ?? null;
    const dailyServiceFormName =
        companyForms.find((form) => form.id === dailyServiceFormId)?.name || 'Daily Service Form';

    // Auto-trigger inspection or daily service when navigated with ?action= param,
    // or resume an in-progress draft after a mobile reload (e.g. returning from camera).
    useEffect(() => {
        if (!selectedUnitId?.value || !equipmentData || selectedOption) {
            return;
        }

        const action = searchParams.get('action');
        if (action === 'inspect' || action === 'dailyService') {
            if (!localStorage.getItem(LOCAL_STORAGE_KEYS.TIME_INSPECTION_STARTED)) {
                localStorage.setItem(LOCAL_STORAGE_KEYS.TIME_INSPECTION_STARTED, new Date().toISOString());
            }
            setSelectedOption(action);
            return;
        }

        if (!equipmentId) {
            return;
        }

        const draft = loadInspectionDraft(equipmentId, selectedUnitId.value);
        if (draft) {
            syncInspectUrl({ unitId: selectedUnitId.value, action: 'inspect' });
            setSelectedOption('inspect');
        }
    }, [equipmentData, equipmentId, searchParams, selectedOption, selectedUnitId, syncInspectUrl]);

    // TODO: make sure the specific unit photo is showing once the unit has been picked, while in the "unit selection" use the category photo

    useEffect(() => {
        const getEquipment = async () => {
            if (!equipmentId) {
                return;
            }
            try {
                // Get equipment from context instead of GraphQL
                const equipmentFromContext = companyGroupedEquipmentData.find((eq) => eq.id === equipmentId);

                if (equipmentFromContext) {
                    setEquipmentData(equipmentFromContext);

                    if (network.online && companyId) {
                        // Cache equipment data
                        localStorage.setItem(
                            `equipment_${equipmentId}`,
                            JSON.stringify({ data: { getEquipment: equipmentFromContext } }),
                        );

                        // Get discrepancies (unchanged)
                        const discrepancyList: tDiscrepancyItem[] = [];
                        const discrepanciesResponse = await authClient.graphql({
                            query: discrepanciesByEquipmentIdQuery,
                            variables: { equipmentId, limit: 100, sortDirection: 'DESC' as ModelSortDirection },
                        });

                        discrepanciesResponse.data.listDiscrepancyByEquipmentIdAndUpdatedAt.items.forEach((item) => {
                            const newItem = {
                                equipmentId: item.equipmentId,
                                equipmentName: `${equipmentFromContext?.name ? equipmentFromContext.name : ''} ${selectedUnitId ? `- ${selectedUnitId.label}` : ''}`,
                                unitId: item.unitId,
                                status: item.status,
                            };
                            discrepancyList.push(newItem);
                        });
                        setDiscrepancyStatus(discrepancyList);
                    }
                } else if (!network.online) {
                    // Fallback to localStorage when offline
                    const cachedData = JSON.parse(localStorage.getItem(`equipment_${equipmentId}`) || '{}');
                    setEquipmentData(cachedData?.data?.getEquipment);
                }

                // Get last report data (unchanged)
                if (selectedUnitId) {
                    const lastReportLoggedForEquipment = await authClient.graphql({
                        query: reportsByUnitIdQuery,
                        variables: {
                            unitId: selectedUnitId.value,
                            limit: 1,
                            sortDirection: 'DESC' as ModelSortDirection,
                        },
                    });
                    setLastReportData(lastReportLoggedForEquipment?.data?.reportsByUnitId?.items[0]);
                } else {
                    const lastReportLoggedForEquipment = await authClient.graphql({
                        query: reportsByEquipmentIdQuery,
                        variables: { equipmentId, limit: 1, sortDirection: 'DESC' as ModelSortDirection },
                    });
                    setLastReportData(lastReportLoggedForEquipment?.data?.reportsByEquipmentId?.items[0] as Report);
                }
            } catch (error) {
                console.log(error);
                Sentry.captureException(error);
            } finally {
                setIsPageLoading(false);
            }
        };

        if (companyId && companyGroupedEquipmentData.length > 0) {
            getEquipment();
        }
    }, [company, companyOwner, equipmentId, selectedUnitId, network.online, companyGroupedEquipmentData]);

    const applySelectedUnit = useCallback(
        (unit: SingleUnit) => {
            skipUnitRestoreRef.current = false;
            setSelectedUnitId({
                label: unit.description ? `${unit.name} - ${unit.description}` : unit.name || '',
                value: unit.id || '',
            });
            setSelectedUnit(unit);
            const activeAction = searchParams.get('action');
            const preserveAction =
                activeAction === 'inspect' || activeAction === 'dailyService' ? activeAction : null;
            syncInspectUrl({ unitId: unit.id || '', action: preserveAction });
        },
        [searchParams, syncInspectUrl],
    );

    useEffect(() => {
        skipUnitRestoreRef.current = false;
    }, [equipmentId]);

    useEffect(() => {
        if (!equipmentData?.units?.length || selectedUnitId) {
            return;
        }

        if (skipUnitRestoreRef.current) {
            return;
        }

        if (equipmentData.units.length === 1) {
            applySelectedUnit(equipmentData.units[0] as SingleUnit);
            return;
        }

        const unitIdParam = searchParams.get('unitId');
        if (unitIdParam) {
            const matchedFromQuery = equipmentData.units.find((u) => u?.id === unitIdParam);
            if (matchedFromQuery) {
                applySelectedUnit(matchedFromQuery as SingleUnit);
            }
        }
    }, [applySelectedUnit, equipmentData, searchParams, selectedUnitId]);

    useEffect(() => {
        if (equipmentId) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.INSPECT, equipmentId);
        }
    }, [equipmentId]);

    const getPrimaryDiscrepancyStatus = (): string | null => {
        if (equipmentData?.units?.length && !selectedUnitId) {
            return null;
        }
        const matches =
            discrepancyStatus?.filter((item) =>
                selectedUnitId
                    ? item.equipmentId === equipmentId && item.unitId === selectedUnitId?.value
                    : item.equipmentId === equipmentId,
            ) ?? [];
        const openMatches = matches.filter((item) => item.status !== ITEM_STATUS_CODES.ok);
        if (!openMatches.length) {
            return null;
        }
        const statusPriority = [ITEM_STATUS_CODES.inop, ITEM_STATUS_CODES.under_review];
        for (const status of statusPriority) {
            if (openMatches.some((item) => item.status === status)) {
                return status;
            }
        }
        return openMatches[0]?.status ?? null;
    };

    const primaryDiscrepancyStatus = getPrimaryDiscrepancyStatus();
    const isOutOfService = primaryDiscrepancyStatus === ITEM_STATUS_CODES.inop;
    const openIssueCount = countOpenIssues(discrepancyStatus, equipmentId, selectedUnitId?.value ?? null);
    const locationLabel =
        companyLocations.find((location) => location.id === selectedUnit?.locationId)?.name ?? 'Unassigned';
    const summaryImageKey = selectedUnit?.id ?? equipmentData?.id;
    const summaryImageUrl = summaryImageKey ? imageUrls[summaryImageKey] : undefined;
    const lastInspectedAt =
        lastInspectionReport?.inspectionCompleted ??
        lastInspectionReport?.createdAt ??
        lastInspectionReport?.inspectionStarted ??
        null;
    const lastInspectorName = lastInspectionReport?.userName ?? lastReportData?.name ?? null;
    const equipmentImageUrl = equipmentData?.id ? imageUrls[equipmentData.id] : undefined;

    const resolveUnitLocationName = useCallback(
        (unit: SingleUnit) => companyLocations.find((location) => location.id === unit.locationId)?.name ?? null,
        [companyLocations],
    );
    const hasDailyServiceForm = Boolean(
        selectedUnitId
            ? equipmentData?.units?.find((unit) => unit?.id === selectedUnitId.value)?.dailyServiceLogFormId
            : equipmentData?.units?.some((unit) => unit?.dailyServiceLogFormId),
    );

    useEffect(() => {
        if (!selectedUnitId?.value) {
            setLastInspectionReport(null);
            return;
        }

        if (selectedOption) {
            return;
        }

        let isCancelled = false;

        (async () => {
            try {
                const report = await fetchLatestInspectionReportForUnit(selectedUnitId.value);
                if (!isCancelled) {
                    setLastInspectionReport(report);
                }
            } catch (error) {
                console.error('Failed to fetch latest inspection report:', error);
                Sentry.captureException(error);
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [selectedUnitId?.value, selectedOption]);

    const storeLastReportForSession = () => {
        if (!lastReportData?.id) {
            return;
        }

        localStorage.setItem(
            LOCAL_STORAGE_KEYS.LAST_REPORT,
            JSON.stringify({
                [lastReportData.id]: {
                    ...lastReportData,
                    equipmentName: equipmentData?.name,
                },
            }),
        );
    };

    const markInspectionStarted = () => {
        localStorage.setItem(LOCAL_STORAGE_KEYS.TIME_INSPECTION_STARTED, new Date().toISOString());
    };

    const handleBeginInspection = () => {
        storeLastReportForSession();
        markInspectionStarted();
        syncInspectUrl({ unitId: selectedUnitId?.value ?? null, action: 'inspect' });
        setSelectedOption('inspect');
    };

    const handleRecordDailyService = () => {
        storeLastReportForSession();
        markInspectionStarted();
        syncInspectUrl({ unitId: selectedUnitId?.value ?? null, action: 'dailyService' });
        setSelectedOption('dailyService');
    };

    const handleViewLastReport = () => {
        const latestInspectionId = lastInspectionReport?.id;

        if (latestInspectionId) {
            const reportPath = buildReportPath({
                id: latestInspectionId,
                source: 'InspectionReport',
                slugLabel: selectedUnit?.name || equipmentData?.name,
            });

            if (guard) {
                guard.tryNavigate(reportPath);
            } else {
                navigate(reportPath);
            }
            return;
        }

        if (!lastReportData?.id) {
            return;
        }

        localStorage.setItem(
            LOCAL_STORAGE_KEYS.REPORT,
            JSON.stringify({
                [lastReportData.id]: {
                    ...lastReportData,
                    equipmentName: equipmentData?.name,
                },
            }),
        );

        const reportPath = buildReportPath({
            id: lastReportData.id,
            source: lastReportData.dailyServiceLogReport ? 'DailyServiceReport' : 'LegacyReport',
            slugLabel: lastReportData.unitName || lastReportData.equipmentName,
        });

        if (guard) {
            guard.tryNavigate(reportPath);
        } else {
            navigate(reportPath);
        }
    };

    const handleViewReportHistory = () => {
        const unitId = selectedUnitId?.value;
        if (!unitId) {
            return;
        }

        seedUnitReportFilters(equipmentId ?? '', unitId);
        localStorage.setItem('reportsActiveTab', 'inspection');
        const reportsPath = buildUnitReportsPath(unitId, {
            tab: 'inspection',
            equipmentCategoryId: equipmentId ?? undefined,
        });

        if (guard) {
            guard.tryNavigate(reportsPath);
        } else {
            navigate(reportsPath);
        }
    };

    const getImageUrl = async (imagePath: string, equipmentId: string) => {
        try {
            if (!imageUrls[equipmentId] && imagePath) {
                const photoUrl = await getUrl({
                    path: imagePath,
                });
                setImageUrls((prev) => ({
                    ...prev,
                    [equipmentId]: photoUrl.url.href,
                }));
            }
        } catch (error) {
            console.error('Error fetching image:', error);
            Sentry.captureException(error);
        }
    };

    const getUnitImageUrl = async (unit: SingleUnit) => {
        if (!unit.image || imageUrls[unit.id]) return;

        try {
            const photoUrl = await getUrl({ path: unit.image });
            setImageUrls((prev) => ({
                ...prev,
                [unit.id]: photoUrl.url.href,
            }));
        } catch (error) {
            console.error('Error fetching unit image:', error);
        }
    };

    useEffect(() => {
        if (equipmentData?.image) {
            getImageUrl(equipmentData?.image, equipmentData.id);
        }
    }, [equipmentData?.image, equipmentData?.id]);

    useEffect(() => {
        if (selectedUnit?.image) {
            getUnitImageUrl(selectedUnit);
        }
    }, [selectedUnit]);

    // const detectIncognito = async (): Promise<boolean> => {
    //   try {
    //     // First detect if we're on mobile
    //     const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    //     console.log('Device type:', isMobile ? 'mobile' : 'desktop');

    //     // Test localStorage
    //     console.log('Testing localStorage...');
    //     try {
    //       localStorage.setItem('test', '1');
    //       localStorage.removeItem('test');
    //       console.log('localStorage test passed');
    //     } catch (e) {
    //       console.log('localStorage test failed');
    //       return true;
    //     }

    //     // Test IndexedDB
    //     console.log('Testing IndexedDB...');
    //     const isPrivate = await new Promise<boolean>((resolve) => {
    //       const db = indexedDB.open('test');
    //       db.onsuccess = () => {
    //         db.result.close();
    //         indexedDB.deleteDatabase('test');
    //         console.log('IndexedDB test passed');
    //         resolve(false);
    //       };
    //       db.onerror = () => {
    //         console.log('IndexedDB test failed');
    //         resolve(true);
    //       };
    //     });

    //     // Chrome/Chromium based browsers
    //     if (/Chrome/.test(navigator.userAgent)) {
    //       console.log('Chrome/Chromium detected');
    //       if ('storage' in navigator && 'estimate' in navigator.storage) {
    //         const { quota } = await navigator.storage.estimate();
    //         console.log('Storage quota:', quota);

    //         // Different quota thresholds for mobile and desktop
    //         const quotaThreshold = isMobile ? 60000000 : 120000000; // 60MB for mobile, 120MB for desktop
    //         if (quota && quota < quotaThreshold) {
    //           console.log('Small storage quota detected, likely incognito');
    //           return true;
    //         }
    //       }
    //     }

    //     // Safari (iOS and Desktop)
    //     if (/Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)) {
    //       console.log('Safari detected');
    //       try {
    //         if (isMobile) {
    //           // iOS Safari private mode detection
    //           const storage = window.sessionStorage;
    //           storage.setItem('test', '1');
    //           storage.removeItem('test');

    //           // Try to write a larger chunk to storage
    //           try {
    //             const testKey = "test";
    //             const testValue = '0'.repeat(1024 * 1024); // 1MB test string
    //             localStorage.setItem(testKey, testValue);
    //             localStorage.removeItem(testKey);
    //           } catch (e) {
    //             console.log('iOS Safari storage capacity test failed - likely private mode');
    //             return true;
    //           }
    //         } else {
    //           // Desktop Safari private mode detection
    //           if (window.localStorage.length) {
    //             const testKey = "test";
    //             localStorage.setItem(testKey, "1");
    //             localStorage.removeItem(testKey);
    //           }
    //         }
    //       } catch (e) {
    //         console.log('Safari storage test failed - likely private mode');
    //         return true;
    //       }
    //     }

    //     // Additional mobile-specific checks
    //     if (isMobile) {
    //       // Test for typical mobile private browsing limitations
    //       try {
    //         // Test for service worker availability (often limited in private mode)
    //         if ('serviceWorker' in navigator) {
    //           const registration = await navigator.serviceWorker.register('/empty-worker.js');
    //           registration.unregister();
    //         } else {
    //           console.log('Service Worker not available - possible private mode indicator');
    //           return true;
    //         }
    //       } catch (e) {
    //         console.log('Service Worker test failed - possible private mode');
    //         // Don't return true here as service workers might be blocked for other reasons
    //       }
    //     }

    //     return isPrivate;
    //   } catch (e) {
    //     console.error('Error in detection:', e);
    //     return false;
    //   }
    // };

    // Effect to check browser mode on component mount
    // useEffect(() => {
    //   const checkBrowserMode = async () => {
    //     const isPrivate = await detectIncognito();
    //     setIsPrivateBrowser(isPrivate);
    //     console.log("is private", isPrivate)
    //   };

    //   checkBrowserMode();
    // }, []);

    // const PrivateBrowserWarningModal = ({
    //   visible,
    //   onDismiss,
    //   onContinue
    // }: {
    //   visible: boolean;
    //   onDismiss: () => void;
    //   onContinue: () => void;
    // }) => {
    //   const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    //   return (
    //     <Modal
    //       visible={visible}
    //       onDismiss={onDismiss}
    //       size="medium"
    //       header="Private Browsing Warning"
    //     >
    //       <SpaceBetween size="l">
    //         <Alert type="warning">
    //           You appear to be using a private/incognito browser window on your {isMobile ? 'mobile device' : 'computer'}.
    //           This may cause issues with:
    //           <ul>
    //             <li>Saving inspection progress</li>
    //             <li>Storing media files (photos/videos)</li>
    //             <li>Offline functionality</li>
    //             {isMobile && <li>Camera and microphone access</li>}
    //           </ul>
    //         </Alert>
    //         <Box>
    //           For the best experience, we recommend:
    //           <ul>
    //             <li>Using a regular (non-private) browser window</li>
    //             <li>Ensuring your browser allows local storage</li>
    //             <li>Enabling camera/microphone permissions if needed</li>
    //             {isMobile && <li>Keeping the app active while using it</li>}
    //           </ul>
    //         </Box>
    //         <SpaceBetween size="xs" direction="horizontal" alignItems="center">
    //           <Button variant="link" onClick={onDismiss}>
    //             Cancel and switch browsers
    //           </Button>
    //           <Button variant="primary" onClick={onContinue}>
    //             Continue anyway
    //           </Button>
    //         </SpaceBetween>
    //       </SpaceBetween>
    //     </Modal>
    //   );
    // };

    // const handleBeginInspection = () => {
    //   console.log("is private browser", isPrivateBrowser)
    //   if (isPrivateBrowser) {

    //     setIsPrivateBrowserModalOpen(true);
    //     console.log("is opening modal")
    //   } else {
    //     // Your existing begin inspection logic
    //     proceedWithInspection();
    //   }
    // };

    // // Function to handle continuing anyway
    // const handleContinueAnyway = () => {
    //   setIsPrivateBrowserModalOpen(false);
    //   proceedWithInspection();
    // };

    // const proceedWithInspection = () => {
    //   setSelectedOption("inspect")
    //   localStorage.setItem(
    //     LOCAL_STORAGE_KEYS.LAST_REPORT,
    //     JSON.stringify({
    //       [lastReportData?.id as string]: {
    //         ...lastReportData,
    //         equipmentName: equipmentData?.name,
    //       },
    //     })
    //   );

    //   localStorage.setItem(LOCAL_STORAGE_KEYS.TIME_INSPECTION_STARTED, new Date().toISOString())
    // }

    if (isPageLoading) {
        return (
            <div className="page-container inspection-flow-page">
                <TEIQspinner variant="lg" label="Loading inspection" />
            </div>
        );
    }

    return (
                <>
                    {!selectedOption ? (
                        <InspectionFlowPageShell
                            onBack={handleInspectionFlowNavBack}
                            backLabel={inspectionFlowBackLabel}
                        >
                            {equipmentData && needsUnitSelection && (
                                <InspectionFlowUnitPicker
                                    equipmentData={equipmentData}
                                    equipmentImageUrl={equipmentImageUrl}
                                    resolveLocationName={resolveUnitLocationName}
                                    onSelectUnit={(unit) => {
                                        setLastReportData(null);
                                        applySelectedUnit(unit);
                                    }}
                                />
                            )}
                            {equipmentData && selectedUnit && selectedUnitId && !needsUnitSelection && (
                                <InspectionOptionsHub
                                    equipmentData={equipmentData}
                                    selectedUnit={selectedUnit}
                                    imageUrl={summaryImageUrl}
                                    categoryLabel={equipmentData.name}
                                    locationLabel={locationLabel}
                                    lastInspectedAt={lastInspectedAt}
                                    lastInspectorName={lastInspectorName}
                                    openIssueCount={openIssueCount}
                                    primaryDiscrepancyStatus={primaryDiscrepancyStatus}
                                    umCatalog={umCatalog}
                                    hasDailyServiceForm={hasDailyServiceForm}
                                    canViewLastReport={Boolean(
                                        lastInspectionReport?.id || lastReportData?.id,
                                    )}
                                    isOutOfService={isOutOfService}
                                    onBeginInspection={handleBeginInspection}
                                    onRecordDailyService={handleRecordDailyService}
                                    onViewLastReport={handleViewLastReport}
                                    onViewReportHistory={handleViewReportHistory}
                                    showChangeUnit={hasMultipleUnits}
                                    onChangeUnit={clearSelectedUnit}
                                />
                            )}
                        </InspectionFlowPageShell>
                    ) : (
                        <>
                            {selectedOption === 'inspect' && (
                                <Inspect
                                    equipmentData={equipmentData as tGroupedEquipment}
                                    equipmentId={equipmentId as string}
                                    selectedUnitId={selectedUnitId}
                                    selectedUnit={selectedUnit as SingleUnit}
                                    backBtnAction={
                                        searchParams.get('returnTo') === 'equipment'
                                            ? handleInspectPageBack
                                            : handleExitInspectionToHub
                                    }
                                    uiVariant="flow"
                                    equipmentTitle={selectedUnit?.name || equipmentData?.name || 'Inspection'}
                                    formSubtitle={`${inspectionFormName} · ${locationLabel}`}
                                    locationLabel={locationLabel}
                                />
                            )}
                            {selectedOption === 'dailyService' && (
                                <DailyServiceLog
                                    equipmentData={equipmentData as tGroupedEquipment}
                                    equipmentId={equipmentId as string}
                                    selectedUnitId={selectedUnitId}
                                    selectedUnit={selectedUnit as SingleUnit}
                                    backBtnAction={
                                        searchParams.get('returnTo') === 'equipment'
                                            ? handleInspectPageBack
                                            : handleExitInspectionToHub
                                    }
                                    uiVariant="flow"
                                    equipmentTitle={selectedUnit?.name || equipmentData?.name || 'Daily Service'}
                                    formSubtitle={`${dailyServiceFormName} · ${locationLabel}`}
                                    locationLabel={locationLabel}
                                />
                            )}
                        </>
                    )}
                </>
    );
};
